import { h, Component } from 'preact';
import { contains, getTouch } from '../lib/util';

const EVENTS = {
	onStart: ['mousedown', 'touchstart'],
	onMove: ['mousemove', 'touchmove'],
	onEnd: ['mouseup', 'touchend'],
	onClick: ['click']
};

const DIRECTIONS = [
	'right',
	'up',
	'left',
	'down'
];

function fireEvent(element, type) {
	let e = document.createEvent('Event');
	e.initEvent(type, true, true);
	element.dispatchEvent(e);
}

/** @param {Object} props
 *	@param {Function<SwipeEvent>} [props.onStart]		called when dragging starts
 *	@param {Function<SwipeEvent>} [props.onChange]		called when drag delta changes
 *	@param {Function<SwipeEvent>} [props.onEnd]			called when drag operation is released
 *	@param {Function<SwipeEvent>} [props.onSwipe]		invoked after any swipe. Get the swipe direction from `event.direction`.
 *	@param {Function<SwipeEvent>} [props.onSwipeLeft]	invoked after a left swipe (cursor/finger moved to the left)
 *	@param {Function<SwipeEvent>} [props.onSwipeRight]	invoked after a right swipe (cursor/finger moved to the right)
 *	@param {Function<SwipeEvent>} [props.onSwipeUp]		invoked after an upward swipe (cursor/finger moved up)
 *	@param {Function<SwipeEvent>} [props.onSwipeDown]	invoked after a downward swipe (cursor/finger moved down)
 */
export default class Swipeable extends Component {
	static defaultProps = {
		distanceThreshold: 200,
		velocityThreshold: 1
	};

	onStart = e => {
		let touch = getTouch(e);

		if (!contains(this.base, touch.target)) return;

		this.start = touch;
		this.time = null;


		if (this.props.preventDefault!==false) e.preventDefault();
	};

	onClick = e => {
		if (this.swipeHandled) {
			if (this.props.preventDefault!==false) { e.preventDefault(); }
			delete this.swipeHandled;
		}
	}

	onMove = e => {
		if (!this.start) return;
		let evt = this.getEvent(e);
		if (!this.time) {
			if (evt.distance>=3) {
				this.time = Date.now();
				this.start = getTouch(e);
				if (this.props.onStart) this.props.onStart(this.getEvent(e));
			}
			return;
		}
		if (this.props.onChange) this.props.onChange(evt);
	};

	onEnd = e => {
		if (!this.start) return;
		let { props, time, start } = this,
			evt = this.getEvent(e),
			directionSuffix = evt.direction ? (evt.direction[0].toUpperCase() + evt.direction.substring(1)) : 'Unrecognized',
			swipeType = 'onSwipe' + directionSuffix;

		this.time = this.start = null;
		if (!time) {
			// Re-fire a click event when this is not considered a swipe and preventedDefault is true is true
			this.props.preventDefault!==false && fireEvent(start.target, 'click');
			return;
		}

		evt.aboveThreshold = time && (evt.distance>props.distanceThreshold || evt.velocity>props.velocityThreshold);
		if (evt.aboveThreshold) {
			if (props.onSwipe && props.onSwipe(evt)!==false) evt.handled = true;
			if (props[swipeType] && props[swipeType](evt)!==false) evt.handled = true;

		}

		this.swipeHandled = true; // Signifys a swipe happened.
		if (props.onEnd) props.onEnd(evt);
	};

	getEvent(e) {
		let touch = getTouch(e),
			time = Date.now() - this.time,
			deltaX = touch.pageX - this.start.pageX,
			deltaY = touch.pageY - this.start.pageY,
			distance = Math.sqrt(deltaX*deltaX + deltaY*deltaY),
			velocity = distance / time,
			quadrant = Math.round(Math.atan2(-deltaY, deltaX) / Math.PI * 2 + 4) % 4,
			direction = DIRECTIONS[quadrant];
		return { deltaX, deltaY, distance, velocity, direction };
	}

	componentDidMount() {
		for (let fn in EVENTS) if (EVENTS.hasOwnProperty(fn)) {
			EVENTS[fn][0] && addEventListener(EVENTS[fn][0], this[fn]);
			EVENTS[fn][1] && addEventListener(EVENTS[fn][1], this[fn]);
		}
	}

	componentWillUnmount() {
		for (let fn in EVENTS) if (EVENTS.hasOwnProperty(fn)) {
			EVENTS[fn][0] && removeEventListener(EVENTS[fn][0], this[fn]);
			EVENTS[fn][1] && removeEventListener(EVENTS[fn][1], this[fn]);
		}
	}

	render({ children }) {
		return children && children[0] || null;
	}
}
